import { useState, useCallback, useEffect } from "react";
import { useURLState } from "./useURLState";

/**
 * Pagination state management hook for Creative system
 * Persists page numbers per tab in localStorage, URL is primary source
 */
export const usePaginationState = () => {
  const { urlState } = useURLState();

  const [pageNumbers, setPageNumbers] = useState(() => {
    try {
      const saved = localStorage.getItem('creatives-page-numbers');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  // Key is just the flat tab name
  const getCurrentKey = useCallback(() => {
    return urlState.tab;
  }, [urlState.tab]);

  // URL page takes precedence, localStorage is fallback
  const getCurrentPage = useCallback(() => {
    if (urlState.pageNumber > 0) {
      return urlState.pageNumber;
    }
    return pageNumbers[urlState.tab] || 1;
  }, [urlState.pageNumber, urlState.tab, pageNumbers]);

  // Update pagination for a specific tab
  const updatePagination = useCallback((tab, page) => {
    setPageNumbers(prev => {
      const newState = { ...prev, [tab]: page };
      try {
        localStorage.setItem('creatives-page-numbers', JSON.stringify(newState));
      } catch (e) {
        console.warn('Failed to save page numbers:', e);
      }
      return newState;
    });
  }, []);

  // Update pagination for the current tab
  const updateCurrentPagination = useCallback((page) => {
    updatePagination(urlState.tab, page);
  }, [updatePagination, urlState.tab]);

  // Sync URL page to localStorage
  useEffect(() => {
    if (urlState.pageNumber > 0) {
      const storedPage = pageNumbers[urlState.tab];
      if (urlState.pageNumber !== storedPage) {
        updateCurrentPagination(urlState.pageNumber);
      }
    }
  }, [urlState.pageNumber, urlState.tab, pageNumbers, updateCurrentPagination]);

  return {
    getCurrentPage,
    getCurrentKey,
    updatePagination,
    updateCurrentPagination,
    pageNumbers,
  };
};

export default usePaginationState;
